import { useState } from 'react';
import { toast } from 'sonner';
import { Upload, Loader2, X, ArrowUp, ArrowDown, Trash2, Plus, Eye, Code } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import api from '@/utils/api';
import { errMsg } from '@/components/jobfairs/fairApi';
import { SafeHtml } from '@/components/jobfairs/SafeHtml';

export const words = (s) => (s || '').replace(/<[^>]+>/g, ' ').trim().split(/\s+/).filter(Boolean).length;

export const TextRow = ({ label, value, onChange, testId, hint, ...rest }) => (
  <div>
    <Label className="text-sm">{label}</Label>
    <Input value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={testId} {...rest} />
    {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
  </div>
);

export const ImageField = ({ fairId, label, value, onChange, testId }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setBusy(true);
    try {
      const r = await api.post(`/job-fairs/admin/${fairId}/assets`, fd);
      onChange(r.data.url);
      toast.success('Image uploaded');
    } catch (e) { toast.error(errMsg(e, 'Upload failed')); } finally { setBusy(false); }
  };
  return (
    <div>
      {label && <Label className="text-sm">{label}</Label>}
      <div className="flex items-center gap-3 mt-1">
        <div className="w-20 h-20 rounded-lg border bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
          {value ? <img src={value} alt="" className="w-full h-full object-contain" data-testid={`${testId}-preview`} /> : <span className="text-xs text-slate-400">No image</span>}
        </div>
        <div className="flex-1 space-y-2 min-w-0">
          <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="Paste image URL or upload" data-testid={`${testId}-url`} />
          <div className="flex gap-2">
            <label className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border cursor-pointer hover:bg-slate-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload
              <input type="file" accept="image/*" className="hidden" data-testid={`${testId}-upload`} onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }} />
            </label>
            {value && <Button type="button" size="sm" variant="ghost" onClick={() => onChange('')} data-testid={`${testId}-clear`}><X className="w-4 h-4" /></Button>}
          </div>
        </div>
      </div>
    </div>
  );
};

const SNIPPETS = [['B', '<strong>', '</strong>'], ['I', '<em>', '</em>'], ['P', '<p>', '</p>'], ['List', '<ul>\n  <li>', '</li>\n</ul>'], ['Link', '<a href="https://" target="_blank">', '</a>']];

export const HtmlField = ({ label, value, onChange, rows = 6, testId, maxWords }) => {
  const [preview, setPreview] = useState(false);
  const insert = (a, b) => onChange(`${value || ''}${a}${b}`);
  const count = words(value);
  return (
    <div>
      <div className="flex items-center justify-between">
        <Label className="text-sm">{label}</Label>
        <div className="flex items-center gap-1">
          {!preview && SNIPPETS.map(([t, a, b]) => <button key={t} type="button" onClick={() => insert(a, b)} className="text-xs px-2 py-0.5 rounded border hover:bg-slate-50">{t}</button>)}
          <button type="button" onClick={() => setPreview(!preview)} data-testid={`${testId}-preview-toggle`} className="text-xs px-2 py-0.5 rounded border hover:bg-slate-50 inline-flex items-center gap-1">
            {preview ? <><Code className="w-3 h-3" />HTML</> : <><Eye className="w-3 h-3" />Preview</>}
          </button>
        </div>
      </div>
      {preview ? <div className="mt-1 min-h-[6rem] rounded-md border p-3 bg-white"><SafeHtml html={value} /></div>
        : <Textarea rows={rows} value={value || ''} onChange={(e) => onChange(e.target.value)} className="mt-1 font-mono text-xs" data-testid={testId} />}
      {maxWords && <p className={`text-xs mt-1 ${count > maxWords ? 'text-red-600' : 'text-slate-400'}`}>{count} / {maxWords} words</p>}
    </div>
  );
};

export const move = (arr, i, d) => {
  const j = i + d;
  if (j < 0 || j >= arr.length) return arr;
  const copy = [...arr];
  [copy[i], copy[j]] = [copy[j], copy[i]];
  return copy;
};

export const RowTools = ({ list, i, onChange, testId }) => (
  <div className="flex gap-1 shrink-0">
    <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => onChange(move(list, i, -1))} data-testid={`${testId}-up-${i}`}><ArrowUp className="w-4 h-4" /></Button>
    <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => onChange(move(list, i, 1))} data-testid={`${testId}-down-${i}`}><ArrowDown className="w-4 h-4" /></Button>
    <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => onChange(list.filter((_, k) => k !== i))} data-testid={`${testId}-remove-${i}`}><Trash2 className="w-4 h-4" /></Button>
  </div>
);

export const AddButton = ({ onClick, label, testId }) => (
  <Button type="button" variant="outline" size="sm" onClick={onClick} data-testid={testId}><Plus className="w-4 h-4 mr-1" />{label}</Button>
);

export const Panel = ({ title, desc, children }) => (
  <div className="bg-white border rounded-xl p-5 space-y-4">
    {title && <div><p className="font-semibold text-slate-900">{title}</p>{desc && <p className="text-xs text-slate-500">{desc}</p>}</div>}
    {children}
  </div>
);
